import {
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  ListItemButton,
  useTheme,
  Tooltip,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Player } from '../types';
import { Box } from '@mui/system';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import { resizeImage } from '../../../api/cloudinary';
import { BsThreeDotsVertical } from 'react-icons/bs';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

type PropTypes = {
  cutoff?: number;
  gameOver: boolean;
  modalView?: boolean;
  onUsernameClick?: (profileId: string) => void;
  sortedPlayers?: Player[];
  profileId: string;
  questionIndex?: number;
  roundOver?: boolean;
  spectate?: boolean;
};

export const PoolPlayLeaderboard = ({
  cutoff,
  gameOver,
  modalView,
  onUsernameClick,
  sortedPlayers,
  profileId,
  questionIndex,
  roundOver,
  spectate,
}: PropTypes) => {
  const MAX_LEADERBOARD_COUNT = spectate ? 1000 : 20;

  // duration 1000 is causing a DOM exception error
  // because a new state is set before the duration can finish.
  const animationParent = useAutoAnimate({}) as any;
  // const [animationParent] = useAutoAnimate<any>({
  //   duration: 250,
  // });
  const [poolPlayers, setPoolPlayers] = useState<Player[]>([]);
  const [poolPlayersIds, setPoolPlayersIds] = useState<string[]>();
  const [playersAbove, setPlayersAbove] = useState<number>();
  const [playersBelow, setPlayersBelow] = useState<number>();
  const [playerSelf, setPlayerSelf] = useState<Player>();
  const [cutoffIndex, setCutoffIndex] = useState<number>();
  const [cutoffPlayers, setCutoffPlayers] = useState<{
    start: number;
    end: number;
  }>();
  const [selectedView, setSelectedView] = useState<'top' | 'cutoff'>('top');
  const [playersViewSpread, setPlayersViewSpread] = useState(10);

  useEffect(() => {
    if (!sortedPlayers) return;
    const cutoffIndex = calcCutoffLine();
    setCutoffIndex(cutoffIndex);
    if (selectedView === 'top') {
      const playersCount = spectate
        ? MAX_LEADERBOARD_COUNT
        : playersViewSpread * 2;
      const sliced = sortedPlayers.slice(0, playersCount);
      setPoolPlayers(sliced);
      return;
    }
    const cutoffPlayersData = {
      start: cutoffIndex + 1 - playersViewSpread,
      end: cutoffIndex + 1 + playersViewSpread,
    };
    const sliced = sortedPlayers.slice(
      cutoffPlayersData.start,
      cutoffPlayersData.end
    );
    setCutoffPlayers(cutoffPlayersData);
    setPoolPlayers(sliced);
  }, [selectedView]);

  useEffect(() => {
    if (!sortedPlayers) return;

    const cutoffIndex = calcCutoffLine();
    setCutoffIndex(cutoffIndex);
    let sliced: Player[];
    if (gameOver || roundOver) {
      sliced = sortedPlayers;
    } else if (
      selectedView === 'cutoff' &&
      cutoff &&
      cutoffIndex < sortedPlayers.length - 11 &&
      sortedPlayers.length > MAX_LEADERBOARD_COUNT
    ) {
      const cutoffPlayersData = {
        start: cutoffIndex + 1 - playersViewSpread,
        end: cutoffIndex + 1 + playersViewSpread,
      };
      sliced = sortedPlayers.slice(
        cutoffPlayersData.start,
        cutoffPlayersData.end
      );
      setCutoffPlayers(cutoffPlayersData);
    } else {
      const playersCount = spectate
        ? MAX_LEADERBOARD_COUNT
        : playersViewSpread * 2;
      sliced = sortedPlayers.slice(0, playersCount);
    }
    setPoolPlayers(sliced);

    if (sortedPlayers.length > MAX_LEADERBOARD_COUNT) {
      if (!spectate) {
        if (!modalView && !gameOver) {
          const playersAbove = getPlayersRankedAbove(sortedPlayers, profileId);
          setPlayerSelf(sortedPlayers[playersAbove]);
          const playersBelow = sortedPlayers.length - playersAbove - 1;
          if (selectedView === 'top' && playersAbove >= playersViewSpread * 2) {
            setPlayersAbove(playersAbove - playersViewSpread * 2);
            setPlayersBelow(playersBelow);
          } else if (
            selectedView === 'cutoff' &&
            playersAbove >= cutoff + playersViewSpread // since if the player is within 10, they would show up in the cutoff view
          ) {
            setPlayersAbove(playersAbove - cutoff);
            setPlayersBelow(playersBelow);
          } else {
            setPlayersAbove(playersAbove);
            setPlayersBelow(sortedPlayers.length - playersViewSpread * 2);
          }
        } else {
          setPlayerSelf(undefined);
          setPlayersAbove(undefined);
          setPlayersBelow(undefined);
        }
      } else {
        setPlayersBelow(sortedPlayers.length - playersViewSpread * 2);
      }
    }
  }, [sortedPlayers, selectedView, playersViewSpread]);

  useEffect(() => {
    if (!poolPlayers) return;
    const poolPlayersIds = poolPlayers.map((p) => p.profileId);
    setPoolPlayersIds(poolPlayersIds);
  }, [poolPlayers]);

  const theme = useTheme();

  const getPlayersRankedAbove = (
    sortedPlayers: Player[],
    profileId: string
  ) => {
    const foundIndex = sortedPlayers.findIndex(
      (p) => p.profileId === profileId
    );
    return foundIndex;
  };

  const nameColor = (player: Player, questionIndex?: number) => {
    if (questionIndex === undefined) return 'white';
    if (player.timerLimitReached) return theme.palette.error.main;
    if (player.currentScore !== null) return theme.palette.underPar.main;
    return 'white';
  };

  const calcCutoffLine = () => {
    if (!cutoff || !sortedPlayers) return;
    const cutoffLine = sortedPlayers?.filter(
      (p) => p.rank && p.rank <= cutoff
    ).length;
    return (cutoffLine || 0) - 1;
  };

  const handleViewToggle = (
    event: React.MouseEvent<HTMLElement>,
    selected: 'top' | 'cutoff'
  ) => {
    setSelectedView(selected);
  };

  const showCutoffLine = (index: number) => {
    if (!sortedPlayers) return;
    if (gameOver && index === cutoffIndex) return true;
    if (spectate && index === cutoffIndex) return true;
    if (
      sortedPlayers.length > MAX_LEADERBOARD_COUNT &&
      selectedView === 'cutoff'
    ) {
      const lineIndex = playersViewSpread === 10 ? 9 : 4;
      if (cutoffIndex && cutoffPlayers && index === lineIndex) return true;
    }
    if (sortedPlayers.length <= MAX_LEADERBOARD_COUNT && index === cutoffIndex)
      return true;
    return false;
  };

  const showPlacementAtTop = () => {
    if (!playerSelf) return false;
    if (
      selectedView === 'cutoff' &&
      cutoffPlayers &&
      playerSelf.rank < cutoffPlayers.end &&
      !poolPlayersIds.includes(profileId)
    ) {
      return true;
    }
    return false;
  };

  const showPlacementAtBottom = () => {
    if (!playerSelf) return false;
    if (selectedView === 'top' && !poolPlayersIds.includes(profileId)) {
      return true;
    }
    if (
      selectedView === 'cutoff' &&
      cutoffPlayers &&
      playerSelf.rank > cutoffPlayers.end
    ) {
      return true;
    }
    return false;
  };

  const handlePlayersViewToggle = () => {
    if (playersViewSpread === 5) {
      setPlayersViewSpread(10);
    } else {
      setPlayersViewSpread(5);
    }
  };

  return (
    <Stack>
      <List dense={true} ref={animationParent}>
        {poolPlayers?.map((p, i) => (
          <Box key={p.profileId}>
            <ListItem
              disablePadding
              sx={{
                border:
                  p.profileId === profileId && !modalView
                    ? '1px solid'
                    : 'none',
                borderColor:
                  p.profileId === profileId && !modalView
                    ? 'primary.dark'
                    : 'none',
              }}
            >
              <ListItemButton
                onClick={() => onUsernameClick && onUsernameClick(p.profileId)}
              >
                <ListItemText
                  primary={p.rank + '.'}
                  sx={{
                    marginRight: 1,
                  }}
                  primaryTypographyProps={{
                    style: {
                      fontWeight: p.profileId === profileId ? 600 : 'none',
                    },
                  }}
                />

                {p.disconnected ? (
                  <Tooltip title="Disconnected" placement="left">
                    <Avatar
                      variant="rounded"
                      sx={{
                        height: 20,
                        width: 20,
                      }}
                    >
                      <PowerOffIcon
                        sx={{
                          color: 'error.main',
                        }}
                      />
                    </Avatar>
                  </Tooltip>
                ) : (
                  <Avatar
                    src={resizeImage(p.avatarPublicId, 50, 50)}
                    variant="rounded"
                    sx={{
                      height: 20,
                      width: 20,
                    }}
                  >
                    {!p.avatarUrl ? p.username.charAt(0) : null}
                  </Avatar>
                )}
                <ListItemText
                  primary={p.username}
                  sx={{
                    marginLeft: 1,
                    marginRight: 2,
                    width: { sm: '150px' },
                  }}
                  primaryTypographyProps={{
                    style: {
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontWeight: p.profileId === profileId ? 600 : 'none',
                      color: nameColor(p, questionIndex),
                    },
                  }}
                />
                {p.currentScore !== undefined && p.currentScore !== null ? (
                  <ListItemText
                    primary={`${p.currentScore > 0 ? '+' : ''}${
                      p.currentScore
                    }`}
                    sx={{ textAlign: 'right' }}
                    primaryTypographyProps={{
                      style: {
                        width: '40px',
                        textAlign: 'left',
                        color: nameColor(p, questionIndex),
                      },
                    }}
                  />
                ) : null}
                <ListItemText
                  primary={`${p.parScore > 0 ? '+' : ''}${p.parScore}`}
                  sx={{ textAlign: 'right' }}
                  primaryTypographyProps={{
                    style: {
                      fontWeight: 600,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            {showCutoffLine(i) ? (
              <Divider
                sx={{
                  fontSize: '12px',
                  color: theme.palette.underPar.main,
                  '&::before, &::after': {
                    borderColor: theme.palette.underPar.main,
                  },
                }}
              >
                cutoff
              </Divider>
            ) : null}
          </Box>
        ))}
      </List>
    </Stack>
  );
};
